import { Button, Empty, Flex, Grid, Skeleton, Table, Tag, Typography } from 'antd'
import type { ColumnType } from 'antd/es/table'
import { FilterOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { formatMoneyValue } from '@/shared/lib/money'
import { formatDateTime } from '@/shared/lib/format'
import { EmptyState } from '@/shared/ui/EmptyState'
import { LoadError } from '@/shared/ui/LoadError'
import type { AgentOperation } from './profileApi'
import type { OperationsState } from './useMyAccount'

/**
 * ⚠️ Rang natijaga bog'lanadi, amal turiga EMAS. Kassir ro'yxatga
 * bitta savol bilan qaraydi: «o'tdimi yoki yo'q». Turni matn aytadi.
 */
export function ResultTag({ result }: { result: string }) {
  const { t } = useTranslation()
  const color = result === 'OK' ? 'green' : result === 'PARTIAL' ? 'gold' : 'red'
  return <Tag color={color}>{t(`myOperations.result.${result}`, { defaultValue: result })}</Tag>
}

export function KindText({ kind }: { kind: string }) {
  const { t } = useTranslation()
  return <>{t(`myOperations.kind.${kind}`, { defaultValue: kind })}</>
}

/**
 * ⚠️ Login BO'SH bo'lishi mumkin va bu nosozlik emas: audit jurnali
 * o'zgarmas, eski yozuvlarga `login` keyin qo'shilmagan. O'shanda
 * chiziqcha ko'rsatiladi — «noma'lum abonent» degan yolg'on emas.
 */
function Subscriber({ row }: { row: AgentOperation }) {
  const { t } = useTranslation()
  return <>{row.subscriberLogin ?? t('common.dash')}</>
}

/**
 * ⚠️ `null` — aktor SAQLANMAGAN yozuv. Maydon BO'SH qoladi: «SYSTEM»
 * yoki «—» yozish kim to'ldirganini bilamiz degan yolg'on da'vo bo'lardi
 * (egasi qarori, 2026-08-19).
 */
function Actor({ row }: { row: AgentOperation }) {
  const { t } = useTranslation()
  if (row.actor !== null) return <>{row.actor}</>
  return (
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {t('myOperations.actorUnknown')}
    </Typography.Text>
  )
}

interface OperationsListProps {
  state: OperationsState
  onLoadMore: () => void
  /** Qatorga bosilganda batafsil ko'rinish ochiladi (`OperationDetail`) */
  onSelect: (row: AgentOperation) => void
  /**
   * `deposits` — depozit ustunlari: sana · summa · KIM · havola.
   *
   * ⚠️ Abonent ustuni u yerda ATAYLAB yo'q: depozitni ADMIN to'ldiradi
   * va qarshi tomon abonent emas. Bo'sh ustun «abonent noma'lum» bo'lib
   * o'qilardi.
   *
   * `grants` — «Berilgan paketlar»: sana · abonent · PAKET · summa ·
   * natija (egasi talabi, 2026-08-20).
   */
  variant?: 'all' | 'deposits' | 'grants'
  /**
   * Qatordagi paketning nomi; `null` — nom NA katalogda, NA javobda.
   *
   * ⚠️ Funksiya TASHQARIDAN keladi va bu qatlam qarori:
   * nom `/api/catalog/plans` dan olinadi, u esa `features/cashdesk`
   * da. `features/*` bir-birini import qilmaydi (07-FRONTEND §6),
   * shuning uchun ikkalasini SAHIFA bog'laydi.
   */
  planLabel?: (row: AgentOperation) => string | null
  /**
   * Ro'yxat filtrlanganmi. ⚠️ Bo'sh natijaning IKKI SABABI bor va ular
   * ARALASHTIRILMAYDI: «amal bo'lmagan» — bu davrda ish yo'q, «filtr
   * hech narsa topmadi» — ish bor, tanlov tor.
   */
  filtered?: boolean
  onResetFilter?: () => void
}

/**
 * ⚠️ QATOR BOSILADIGAN, lekin AMAL USTUNI YO'Q (§5: «amal ustuni yo'q»).
 * Bosish faqat KO'RSATADI — pul amali yoki qayta yuborish qilmaydi,
 * shuning uchun tasodifiy tegish hech narsani buzmaydi.
 *
 * ⚠️ Klaviatura ham ishlaydi (`role="button"` + `Enter`/`Space`): §8
 * fokus zanjirini talab qiladi va telefondagi ekran o'quvchi bosiladigan
 * `div` ni aks holda umuman e'lon qilmasdi.
 */
export function OperationsList({
  state,
  onLoadMore,
  onSelect,
  variant = 'all',
  planLabel,
  filtered = false,
  onResetFilter,
}: OperationsListProps) {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false
  const deposits = variant === 'deposits'
  const grants = variant === 'grants'

  if (state.loading) {
    return <Skeleton active paragraph={{ rows: 4 }} />
  }
  if (state.error) {
    return <LoadError titleKey="myOperations.loadFailed" error={state.error} />
  }

  /**
   * ⚠️ Nom topilmasa CHIZIQCHA, «Noma'lum paket» EMAS va `planId` ning
   * qisqartmasi ham emas. Qisqartilgan `0f9c8e2a…` ustunda NOMGA
   * o'xshab turadi va kassir uni o'qishga urinardi — bo'shliq esa
   * ro'yxatning qolgan ustunlari bilan bir xil o'qiladi: «server bu
   * yerda hech narsa aytmadi». To'liq id yo'qolmaydi — u batafsil
   * ko'rinishda, sababi bilan birga turadi (`OperationDetail`).
   */
  const planText = (row: AgentOperation) => planLabel?.(row) ?? t('common.dash')

  const rows = state.page?.rows ?? []
  if (rows.length === 0) {
    if (filtered) {
      return (
        <EmptyState
          icon={<FilterOutlined />}
          title={t('myOperations.emptyFiltered')}
          description={t('myOperations.emptyFilteredHint')}
          {...(onResetFilter
            ? { actionLabel: t('myOperations.filter.clear'), onAction: onResetFilter }
            : {})}
        />
      )
    }
    /**
     * ⚠️ «Berilgan paketlar» ning BO'SHLIGI BOSHQA narsani anglatadi va
     * shuning uchun matni ham boshqa. O'lchandi (Staging, 2026-08-21):
     * reseller paket bersa ham (`POST /api/grants` → `201`) ro'yxat
     * BO'SH qoladi — server `GRANT_ISSUE` auditiga sherik tugunini
     * faqat `OWN` doirali kassirda yozadi, reseller doirasi esa
     * `SUBTREE`. Umumiy «amal bo'lmagan» matni bu yerda «hech kimga
     * bermadim» bo'lib o'qilardi, ya'ni YOLG'ON xulosa berardi.
     */
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={grants ? t('grants.empty') : t('myOperations.empty')}
      >
        {grants ? (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {t('grants.emptyHint')}
          </Typography.Text>
        ) : null}
      </Empty>
    )
  }

  const more = state.page?.hasNext ? (
    <Button block size="large" style={{ height: 44 }} onClick={onLoadMore}>
      {t('myOperations.loadMore')}
    </Button>
  ) : null

  // Bosish mumkinligi AYTILADI: qatorda ko'rsatkich (chevron, tugma)
  // yo'q va §5 uni qo'shishni taqiqlaydi («amal ustuni yo'q»), ya'ni
  // aks holda batafsil ko'rinishni hech kim topmasdi.
  const hint = (
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {t('myOperations.detail.hint')}
    </Typography.Text>
  )

  // Telefonda jadval emas, kartochka — besh ustun 390px da bir-biriga
  // yopishadi (`SubscriberSearch` bilan ayni qaror).
  if (isMobile) {
    return (
      <Flex vertical gap={8}>
        {hint}
        {rows.map((row, index) => (
          <div
            key={`${row.at}-${row.txnId ?? index}`}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(row)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onSelect(row)
              }
            }}
            style={{
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              paddingBottom: 8,
              cursor: 'pointer',
            }}
          >
            <Flex justify="space-between" align="baseline" gap={8}>
              <Typography.Text strong>
                {deposits ? <Actor row={row} /> : <Subscriber row={row} />}
              </Typography.Text>
              <Typography.Text strong>
                {row.amount ? formatMoneyValue(row.amount) : t('common.dash')}
              </Typography.Text>
            </Flex>
            {/* Uzun nom qator balandligini o'zgartirmasin — jadval
                ustunidagi `ellipsis` bilan ayni sabab. */}
            {grants ? (
              <div
                style={{
                  fontSize: 13,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {planText(row)}
              </div>
            ) : null}
            <Flex justify="space-between" align="center" gap={8}>
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                {formatDateTime(row.at)}
                {/* «Berilgan paketlar» da tur HAR qatorda bir xil —
                    uni takrorlash shovqin. */}
                {deposits || grants ? null : (
                  <>
                    {' · '}
                    <KindText kind={row.kind} />
                  </>
                )}
              </Typography.Text>
              <ResultTag result={row.result} />
            </Flex>
            {deposits && row.reference !== null ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                {row.reference}
              </Typography.Text>
            ) : null}
          </div>
        ))}
        {more}
      </Flex>
    )
  }

  const atColumn: ColumnType<AgentOperation> = {
    title: t('myOperations.col.at'),
    render: (_v, row) => formatDateTime(row.at),
    width: 170,
  }
  const amountColumn: ColumnType<AgentOperation> = {
    title: t('myOperations.col.amount'),
    align: 'right',
    render: (_v, row) => (row.amount ? formatMoneyValue(row.amount) : t('common.dash')),
    width: 140,
  }
  const resultColumn: ColumnType<AgentOperation> = {
    title: t('myOperations.col.result'),
    render: (_v, row) => <ResultTag result={row.result} />,
    width: 120,
  }
  /**
   * ⚠️ `ellipsis` MAJBURIY: `sardor.karimov@uy` kabi login tor ustunda
   * ikki-uch qatorga bo'linib, qator balandligini o'zgartirib turardi
   * (2026-08-20 auditi, §1.5). Kenglik qotirilmagan — bu ustun qolgan
   * joyni oladi.
   */
  const subscriberColumn: ColumnType<AgentOperation> = {
    title: t('myOperations.col.subscriber'),
    render: (_v, row) => <Subscriber row={row} />,
    ellipsis: true,
  }

  const planColumn: ColumnType<AgentOperation> = {
    title: t('myOperations.col.plan'),
    render: (_v, row) => planText(row),
    ellipsis: true,
  }

  const columns: ColumnType<AgentOperation>[] = deposits
    ? [
        atColumn,
        amountColumn,
        { title: t('myOperations.col.actor'), render: (_v, row) => <Actor row={row} />, width: 200 },
        {
          title: t('myOperations.col.reference'),
          render: (_v, row) => row.reference ?? t('common.dash'),
          ellipsis: true,
        },
      ]
    : grants
      ? [atColumn, subscriberColumn, planColumn, amountColumn, resultColumn]
      : [
          atColumn,
          subscriberColumn,
          {
            title: t('myOperations.col.kind'),
            render: (_v, row) => <KindText kind={row.kind} />,
            width: 160,
          },
          amountColumn,
          resultColumn,
        ]

  return (
    <Flex vertical gap={12}>
      {hint}
      <Table<AgentOperation>
        dataSource={rows}
        rowKey={(row) => `${row.at}-${row.txnId ?? row.reference ?? row.kind}`}
        pagination={false}
        size="small"
        onRow={(row) => ({ onClick: () => onSelect(row), style: { cursor: 'pointer' } })}
        columns={columns}
      />
      {more}
    </Flex>
  )
}
